import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ShieldCheckIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { LoadingSpinner } from '@/components/common';


type LoginStep = 'credentials' | 'totp';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, setPermissions, setPlatformPermissions, setPendingTwoFactor } = useAuthStore();

  const [step, setStep] = useState<LoginStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [preAuthToken, setPreAuthToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const totpInputRef = useRef<HTMLInputElement>(null);

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.login(email, password);
      const data = response.data;

      if (data.requires_2fa) {
        // 2FA required - show TOTP input
        setPreAuthToken(data.pre_auth_token);
        setPendingTwoFactor(true, data.pre_auth_token);
        setStep('totp');
        setTimeout(() => totpInputRef.current?.focus(), 100);
      } else {
        // Normal login
        const { user, tokens, permissions, platformPermissions } = data;
        login(user, tokens);
        if (permissions) {
          setPermissions(permissions);
        }
        setPlatformPermissions(platformPermissions || []);
        toast.success(`Bienvenido, ${user.profile?.first_name || user.email}`);
        navigate('/');
      }
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al iniciar sesión';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!totpCode) {
      toast.error('Ingresa el código de verificación');
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.verify2fa(preAuthToken, totpCode);
      const { user, tokens, permissions, platformPermissions } = response.data;

      setPendingTwoFactor(false);
      login(user, tokens);
      if (permissions) {
        setPermissions(permissions);
      }
      setPlatformPermissions(platformPermissions || []);
      toast.success(`Bienvenido, ${user.profile?.first_name || user.email}`);
      navigate('/');
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Código inválido';
      toast.error(message);
      setTotpCode('');
      totpInputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setPreAuthToken('');
    setTotpCode('');
    setPendingTwoFactor(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-vida-900 to-gray-900 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-vida-500 rounded-2xl mb-4">
            <span className="text-white font-bold text-3xl">V</span>
          </div>
          <h1 className="text-2xl font-bold text-white">VIDA System</h1>
          <p className="text-gray-400 mt-2">Panel de administración</p>
        </div>

        {/* Login form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {step === 'credentials' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Iniciar sesión</h2>

              <form onSubmit={handleCredentialsSubmit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    className="input"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="label">
                    Contraseña
                  </label>
                  <input
                    id="password"
                    type="password"
                    className="input"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full py-3"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="sm" />
                      Iniciando sesión...
                    </span>
                  ) : (
                    'Iniciar sesión'
                  )}
                </button>

                <div className="text-center">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-vida-600 hover:text-vida-700 font-medium"
                  >
                    ¿Has olvidado tu contraseña?
                  </Link>
                </div>
              </form>
            </>
          )}

          {step === 'totp' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-vida-100 rounded-lg">
                  <ShieldCheckIcon className="w-6 h-6 text-vida-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Verificación 2FA</h2>
                  <p className="text-sm text-gray-500">Ingresa el código de tu app de autenticación</p>
                </div>
              </div>

              <form onSubmit={handleTotpSubmit} className="space-y-5">
                <div>
                  <label htmlFor="totp" className="label">
                    Código de verificación
                  </label>
                  <input
                    ref={totpInputRef}
                    id="totp"
                    type="text"
                    className="input text-center text-2xl tracking-widest font-mono"
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                      setTotpCode(val);
                    }}
                    disabled={loading}
                    autoComplete="one-time-code"
                    maxLength={8}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    También puedes usar un código de respaldo (8 caracteres)
                  </p>
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full py-3"
                  disabled={loading || !totpCode}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="sm" />
                      Verificando...
                    </span>
                  ) : (
                    'Verificar'
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleBackToCredentials}
                  className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  Volver al inicio de sesión
                </button>
              </form>
            </>
          )}

          <p className="mt-6 text-center text-sm text-gray-500">
            ¿Problemas para acceder?{' '}
            <a href="#" className="text-vida-600 hover:text-vida-700 font-medium">
              Contacta al administrador
            </a>
          </p>
        </div>

        <p className="mt-8 text-center text-sm text-gray-500">
          VIDA System &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
